import React, { useState, useEffect, useRef } from 'react';
import { X, Crown, Lightbulb, ArrowRight, Sparkles, MessageCircle } from 'lucide-react';
import { usePlan } from '../../contexts/PlanContext';

const TIPS = {
  th: [
    { title: 'ตั้งแจ้งเตือนราคา', desc: 'คุณสามารถตั้ง Alert ผ่าน Telegram ได้เมื่อหุ้นถึงราคาเป้าหมาย', icon: '🔔' },
    { title: 'ดูปฏิทินปันผล', desc: 'ติดตามวัน XD และวันจ่ายปันผลของหุ้นที่ถืออยู่ในพอร์ต', icon: '📅' },
    { title: 'จำลอง DCA', desc: 'ใช้ DCA Simulator วางแผนซื้อหุ้นรายเดือนดูผลลัพธ์ล่วงหน้า', icon: '📊' },
    { title: 'เช็คต้นทุน FX', desc: 'ดูต้นทุนเฉลี่ย USD/THB และกำไรขาดทุนจากอัตราแลกเปลี่ยน', icon: '💱' },
    { title: 'Watchlist ส่วนตัว', desc: 'สร้างกลุ่มหุ้นที่สนใจเพื่อติดตามราคาแบบ Real-time', icon: '⭐' },
    { title: 'ดูภาพรวมพอร์ต', desc: 'หน้า Analytics แสดงกราฟสัดส่วน กำไร/ขาดทุน และแนวโน้มพอร์ต', icon: '📈' },
    { title: 'เพิ่มเงินปันผลอัตโนมัติ', desc: 'ระบบตรวจจับปันผลอัตโนมัติและเพิ่มเข้าพอร์ตให้ทันที', icon: '🤖' },
    { title: 'ส่งออกข้อมูลภาษี', desc: 'ดาวน์โหลด CSV รายงานภาษีแบบ FIFO สำหรับยื่นภาษี', icon: '🧾' },
    { title: 'แนวรับ-แนวต้าน', desc: 'ตั้ง Support/Resistance Level เพื่อดูจุดซื้อขายที่เหมาะสม', icon: '🎯' },
    { title: 'โหมดซ่อนตัวเลข', desc: 'กดปุ่มตาเพื่อซ่อนยอดเงินทั้งหมด ปลอดภัยเวลาเปิดในที่สาธารณะ', icon: '🙈' },
    { title: 'สถานะตลาดสหรัฐ', desc: 'ดูว่าตลาด US เปิด/ปิด/Pre-market พร้อมนาฬิกานับถอยหลัง', icon: '🕐' },
    { title: 'สลับสกุลเงิน', desc: 'ดูมูลค่าพอร์ตเป็น USD หรือ THB ได้ทุกหน้าด้วยปุ่มเดียว', icon: '🔄' },
    { title: 'แผนช้อนซื้อ (Buy Planner)', desc: 'วางแผนจุดซื้อตามแนวรับ 7 ระดับ พร้อมแชร์แผนผ่าน Telegram', icon: '🛒' },
    { title: 'คำนวณเงินปันผล', desc: 'ใส่จำนวนเงินลงทุน ระบบคำนวณปันผลที่จะได้รับต่อปี/เดือน', icon: '💰' },
    { title: 'จัดสรรพอร์ต (Allocator)', desc: 'ตั้งสัดส่วนเป้าหมายแต่ละสินทรัพย์ ระบบแนะนำว่าควรซื้อเพิ่มตัวไหน', icon: '⚖️' },
    { title: 'จำกัดสัดส่วนรายตัว', desc: 'ตั้งเพดาน % สูงสุดต่อหุ้น ระบบเตือนและบอกว่าควรขายเท่าไรเมื่อถือเกิน', icon: '🛡️' },
    { title: 'ปฏิทินงบการเงิน', desc: 'ดูวันประกาศงบไตรมาสของหุ้นที่ถือ ไม่พลาดข่าว Earnings', icon: '🗓️' },
    { title: 'คำนวณต้นทุนถัวเฉลี่ย', desc: 'จำลองการซื้อเพิ่มเพื่อดูว่าต้นทุนเฉลี่ยจะลดเหลือเท่าไร', icon: '🧮' },
    { title: 'สลับธีมมืด/สว่าง', desc: 'กดไอคอนดวงอาทิตย์/พระจันทร์ เปลี่ยนธีมตามความชอบ', icon: '🌓' },
    { title: 'ติดตามเงินสดในพอร์ต', desc: 'ดูยอดเงินสดคงเหลือ ฝาก/ถอน พร้อม Reconcile ตรวจสอบกับโบรกเกอร์', icon: '💵' },
    { title: 'เลือกวิธีคำนวณต้นทุน', desc: 'สามารถสลับวิธีคำนวณพอร์ตระหว่าง FIFO (ตัดล็อตแรก) หรือ ต้นทุนเฉลี่ย (Average Cost) ได้ที่เมนูโปรไฟล์มุมขวาบน', icon: '⚖️' },
  ],
  en: [
    { title: 'Set Price Alerts', desc: 'Get notified via Telegram when stocks hit your target price', icon: '🔔' },
    { title: 'Dividend Calendar', desc: 'Track XD dates and payment dates for your holdings', icon: '📅' },
    { title: 'DCA Simulator', desc: 'Plan your monthly investments and preview results in advance', icon: '📊' },
    { title: 'Check FX Cost Basis', desc: 'View your average USD/THB cost and FX gains/losses', icon: '💱' },
    { title: 'Personal Watchlists', desc: 'Create groups of stocks to track real-time prices', icon: '⭐' },
    { title: 'Portfolio Analytics', desc: 'View allocation charts, P&L breakdown and portfolio trends', icon: '📈' },
    { title: 'Auto Dividends', desc: 'System automatically detects dividends and adds them to your portfolio', icon: '🤖' },
    { title: 'Tax Export', desc: 'Download FIFO-based CSV tax reports for filing', icon: '🧾' },
    { title: 'Support & Resistance', desc: 'Set price levels to identify optimal buy/sell points', icon: '🎯' },
    { title: 'Privacy Mode', desc: 'Tap the eye icon to hide all balances — safe in public places', icon: '🙈' },
    { title: 'US Market Status', desc: 'See if the US market is open, closed, or in pre-market with a countdown', icon: '🕐' },
    { title: 'Switch Currency', desc: 'View your portfolio in USD or THB on any screen with one tap', icon: '🔄' },
    { title: 'Buy Planner', desc: 'Plan buy points across 7 support levels and share plans via Telegram', icon: '🛒' },
    { title: 'Dividend Calculator', desc: 'Enter your investment amount to see estimated annual/monthly dividends', icon: '💰' },
    { title: 'Portfolio Allocator', desc: 'Set target allocation percentages — system suggests which assets to buy more', icon: '⚖️' },
    { title: 'Allocation Caps', desc: 'Set a max % per stock — get alerted and told how much to trim when over the cap', icon: '🛡️' },
    { title: 'Earnings Calendar', desc: 'View upcoming quarterly earnings dates for your holdings', icon: '🗓️' },
    { title: 'Average Cost Calculator', desc: 'Simulate buying more shares to see how your average cost would change', icon: '🧮' },
    { title: 'Dark / Light Mode', desc: 'Tap the sun/moon icon to switch themes to your preference', icon: '🌓' },
    { title: 'Cash Balance Tracking', desc: 'Track cash balance with deposits, withdrawals, and broker reconciliation', icon: '💵' },
    { title: 'Cost Basis Options', desc: 'Switch your cost basis method between FIFO and Average Cost anytime via the profile menu at top right', icon: '⚖️' },
  ]
};

// Simple flag — resets when page reloads, persists when switching apps
let shownThisSession = false;

export default function WelcomePopup({ isPro, language, darkMode, onUpgrade, onDismiss }) {
  const { planLoading } = usePlan();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('tip');
  const [tip, setTip] = useState(null);
  const [animated, setAnimated] = useState(false);
  const isProRef = useRef(isPro);
  isProRef.current = isPro; // always up-to-date
  const hasShownRef = useRef(false);

  useEffect(() => {
    if (shownThisSession) return;
    // Wait until plan has finished loading before deciding tip vs promo
    if (planLoading) return;
    // If user is already Pro, never show promo
    if (isProRef.current) {
      // Pro user — only show tip, never promo
      hasShownRef.current = true;
      shownThisSession = true;
      const timer = setTimeout(() => {
        const tipList = TIPS[language] || TIPS.en;
        setTip(tipList[Math.floor(Math.random() * tipList.length)]);
        setIsOpen(true);
      }, 800);
      return () => clearTimeout(timer);
    }

    hasShownRef.current = true;
    shownThisSession = true;

    const timer = setTimeout(() => {
      const tipList = TIPS[language] || TIPS.en;
      const randomTip = tipList[Math.floor(Math.random() * tipList.length)];

      // Double-check: only show promo if user is definitively NOT Pro
      if (!isProRef.current && Math.random() < 0.4) {
        setMode('promo');
      }

      setTip(randomTip);
      setIsOpen(true);
    }, 800);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planLoading]);

  const handleClose = () => {
    setIsOpen(false);
    if (onDismiss) onDismiss();
  };

  const handleUpgrade = () => {
    handleClose();
    if (onUpgrade) onUpgrade();
  };

  if (!isOpen || !tip) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/10 backdrop-blur-[3px]" style={{ willChange: 'backdrop-filter' }} />

      <div
        onAnimationEnd={() => setAnimated(true)}
        className={`relative w-full max-w-[340px] rounded-3xl overflow-hidden shadow-2xl ${!animated ? 'animate-in zoom-in-95 fade-in duration-300' : ''} ${
          darkMode
            ? 'bg-gradient-to-br from-slate-800 via-[#1a1f35] to-[#0f172a] border border-slate-700/40'
            : 'bg-gradient-to-br from-white via-indigo-50/30 to-white border border-slate-200/80'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative orbs */}
        <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-[60px] pointer-events-none" style={{ background: darkMode ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.06)' }} />
        <div className="absolute -bottom-12 -left-12 w-32 h-32 rounded-full blur-[50px] pointer-events-none" style={{ background: darkMode ? 'rgba(168,85,247,0.1)' : 'rgba(168,85,247,0.05)' }} />
        {/* Close button */}
        <button
          onClick={handleClose}
          className={`absolute top-3 right-3 z-10 w-7 h-7 flex items-center justify-center rounded-full transition-colors ${
            darkMode ? 'bg-slate-700/60 text-slate-400 hover:text-white' : 'bg-slate-100 text-slate-400 hover:text-slate-700'
          }`}
        >
          <X size={14} />
        </button>

        {mode === 'promo' ? (
          /* ============ PROMOTION MODE ============ */
          <>
            {/* Gradient Header */}
            <div className="relative px-6 pt-6 pb-5 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 text-center overflow-hidden">
              <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")" }}></div>
              <Sparkles className="w-10 h-10 text-amber-300 mx-auto mb-2 drop-shadow-lg" />
              <h3 className="text-lg font-black text-white tracking-tight">
                {language === 'en' ? 'Unlock Pro Features' : 'ปลดล็อกฟีเจอร์ Pro'}
              </h3>
              <p className="text-indigo-100 text-[11px] mt-1.5 opacity-90 leading-relaxed">
                {language === 'en'
                  ? 'Unlimited holdings, alerts, watchlists & more'
                  : 'เพิ่มหุ้นไม่จำกัด แจ้งเตือน Watchlist และอีกมากมาย'}
              </p>
            </div>

            {/* Features */}
            <div className="px-5 py-4 space-y-2.5">
              {[
                language === 'en' ? 'Unlimited holdings & watchlists' : 'เพิ่มหุ้นและ Watchlist ไม่จำกัด',
                language === 'en' ? 'Telegram price alerts' : 'แจ้งเตือนราคาผ่าน Telegram',
                language === 'en' ? 'DCA Simulator — unlimited runs' : 'จำลอง DCA ไม่จำกัดรอบ',
                language === 'en' ? 'Advanced analytics & tax reports' : 'วิเคราะห์ขั้นสูงและรายงานภาษี',
              ].map((text, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    darkMode ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-600'
                  }`}>
                    <Crown size={10} />
                  </div>
                  <span className={`text-xs font-medium ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{text}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="px-5 pb-5">
              <button
                onClick={handleUpgrade}
                className="w-full py-3 rounded-2xl font-bold text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 transition-all shadow-lg shadow-indigo-600/25 flex items-center justify-center gap-2 text-sm"
              >
                {language === 'en' ? 'Try Pro Free for 14 Days' : 'ทดลองใช้ Pro ฟรี 14 วัน'}
                <ArrowRight size={16} />
              </button>
              <button
                onClick={handleClose}
                className={`w-full mt-2 py-2 text-[11px] font-medium ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}
              >
                {language === 'en' ? 'Maybe later' : 'ไว้คราวหลัง'}
              </button>
              <a
                href="https://lin.ee/9Mgwhzg"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full mt-1 py-2 flex items-center justify-center gap-1.5 text-[11px] font-medium transition-colors ${darkMode ? 'text-green-400 hover:text-green-300' : 'text-[#00B900] hover:text-green-700'}`}
              >
                <MessageCircle size={12} />
                {language === 'en' ? 'Contact Admin (LINE)' : 'ติดต่อแอดมิน (LINE)'}
              </a>
            </div>
          </>
        ) : (
          /* ============ TIP OF THE DAY MODE ============ */
          <>
            <div className="px-6 pt-6 pb-1">
              <div className="flex items-center gap-2 mb-4">
                <div className={`p-2 rounded-xl ${
                  darkMode
                    ? 'bg-gradient-to-br from-amber-600/30 to-orange-600/20 text-amber-400 border border-amber-500/30'
                    : 'bg-gradient-to-br from-amber-50 to-orange-50 text-amber-600 border border-amber-100'
                }`}>
                  <Lightbulb size={18} />
                </div>
                <div>
                  <h3 className={`text-sm font-black tracking-tight ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                    {language === 'en' ? 'Tip of the Day' : 'เคล็ดลับวันนี้'}
                  </h3>
                  <p className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {language === 'en' ? 'Did you know?' : 'รู้หรือยัง?'}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 pb-5">
              <div className={`rounded-2xl p-4 border relative overflow-hidden ${
                darkMode ? 'bg-gradient-to-br from-indigo-900/30 via-slate-800/60 to-purple-900/20 border-indigo-500/20' : 'bg-gradient-to-br from-indigo-50/80 via-white to-amber-50/50 border-indigo-100/60 shadow-sm'
              }`}>
                <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full blur-[30px] pointer-events-none" style={{ background: darkMode ? 'rgba(251,191,36,0.08)' : 'rgba(251,191,36,0.1)' }} />
                <div className="text-2xl mb-2 relative z-10">{tip.icon}</div>
                <h4 className={`text-sm font-bold mb-1 relative z-10 ${darkMode ? 'text-slate-100' : 'text-slate-800'}`}>
                  {tip.title}
                </h4>
                <p className={`text-xs leading-relaxed relative z-10 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  {tip.desc}
                </p>
              </div>

              <button
                onClick={handleClose}
                className={`w-full mt-4 py-2.5 rounded-2xl font-bold text-sm transition-all ${
                  darkMode
                    ? 'bg-slate-700/60 text-slate-300 hover:bg-slate-700'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {language === 'en' ? 'Got it!' : 'เข้าใจแล้ว!'}
              </button>
              <a
                href="https://lin.ee/9Mgwhzg"
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full mt-2 py-2 flex items-center justify-center gap-1.5 text-[11px] font-medium transition-colors ${darkMode ? 'text-green-400 hover:text-green-300' : 'text-[#00B900] hover:text-green-700'}`}
              >
                <MessageCircle size={12} />
                {language === 'en' ? 'Contact Admin (LINE)' : 'ติดต่อแอดมิน (LINE)'}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
